import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { RefreshCw, CheckCircle, XCircle } from 'lucide-react';

export default function SocialCallback() {
  const { platform } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Connecting your account...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    if (error) {
      setStatus('error');
      setMessage(`Authorization failed: ${searchParams.get('error_description') || error}`);
      setTimeout(() => navigate('/admin/social'), 3000);
      return;
    }
    
    if (code && state) {
      handleCallback(code, state);
    } else {
      setStatus('error');
      setMessage('Invalid callback - missing authorization code');
      setTimeout(() => navigate('/admin/social'), 3000);
    }
  }, [searchParams, platform]);

  const handleCallback = async (code, state) => {
    try {
      const response = await axios.post(
        `${API}/social/oauth/${platform}/callback`,
        { code, state },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message || `Successfully connected to ${platform}!`);
        setTimeout(() => navigate('/admin/social'), 2000);
      } else {
        throw new Error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to complete connection');
      setTimeout(() => navigate('/admin/social'), 3000);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-8">
        {status === 'processing' && (
          <>
            <RefreshCw className="w-16 h-16 text-cosmic-gold animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-cinzel text-cosmic-gold mb-4">Connecting Account</h2>
            <p className="text-cosmic-goldLight">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-cinzel text-green-400 mb-4">Success!</h2>
            <p className="text-cosmic-goldLight">{message}</p>
            <p className="text-sm text-cosmic-goldLight/60 mt-4">Redirecting to Social Media Manager...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-2xl font-cinzel text-red-400 mb-4">Connection Failed</h2>
            <p className="text-cosmic-goldLight">{message}</p>
            <p className="text-sm text-cosmic-goldLight/60 mt-4">Redirecting back...</p>
          </>
        )}
      </div>
    </div>
  );
}
